import { getStorageItem, setStorageItem } from './utils/xummStorage';

export type AssetClass = 'stable' | 'fiat' | 'rwa' | 'utility' | 'meme' | 'bridge' | 'other';

export type XrplIssuedAsset = {
  id: string;
  symbol: string;
  issuer: string;
  name: string;
  assetClass: AssetClass;
  description?: string;
  icon?: string;
  logoUrl?: string;
  category?: 'settlement' | 'representational' | 'governance' | 'bridge' | 'meme';
};

const STORAGE_KEY = 'uny_asset_registry_v1';

// ─── On-Ledger Issuers ──────────────────────────
// rE85 issues ALL 9 live tokens on XRPL.
// rpP12 is the treasury/distribution wallet — holds tokens, issues nothing.
export const TOKEN_ISSUER  = 'rE85pdvr4icCPh9cpPr1HrSCVJCUhZ1Dqm';
export const TREASURY      = 'rpP12ND2K7ZRzXZBEUnQM2i18tMGytXnW1';

export const DEFAULT_ASSETS: XrplIssuedAsset[] = [
  // ═══════════════════════════════════════════════
  // LIVE ON-LEDGER — Issued by rE85 (TOKEN_ISSUER)
  // ═══════════════════════════════════════════════

  // ─── Settlement / Fiat IOUs ───────────────────
  {
    id: 'usdt-live',
    symbol: 'USDT',
    issuer: TOKEN_ISSUER,
    name: 'USDT (IOU)',
    assetClass: 'stable',
    category: 'settlement',
    description: 'Internal settlement IOU. 185M circulating · 9 trustlines · 8 holders.',
    icon: '₮',
    logoUrl: '/assets/logos/usdt-logo.svg'
  },
  {
    id: 'eur-live',
    symbol: 'EUR',
    issuer: TOKEN_ISSUER,
    name: 'EUR (IOU)',
    assetClass: 'fiat',
    category: 'settlement',
    description: 'Euro-denominated IOU. 50M circulating · 8 trustlines · 4 holders.',
    icon: '€',
    logoUrl: '/assets/logos/eur-logo.svg'
  },
  {
    id: 'usd-live',
    symbol: 'USD',
    issuer: TOKEN_ISSUER,
    name: 'USD (IOU)',
    assetClass: 'fiat',
    category: 'settlement',
    description: 'USD-denominated IOU. 60K circulating · 5 trustlines · 4 holders.',
    icon: '$',
    logoUrl: '/assets/logos/xusd-logo.svg'
  },
  {
    id: 'gbp-live',
    symbol: 'GBP',
    issuer: TOKEN_ISSUER,
    name: 'GBP (IOU)',
    assetClass: 'fiat',
    category: 'settlement',
    description: 'British Pound IOU. 30M circulating · 5 trustlines · 4 holders.',
    icon: '£',
    logoUrl: '/assets/logos/gbp-logo.svg'
  },
  {
    id: 'eurd-live',
    symbol: 'EURD',
    issuer: TOKEN_ISSUER,
    name: 'EURD',
    assetClass: 'fiat',
    category: 'settlement',
    description: 'Euro derivative settlement token. 100K circulating.',
    icon: '€',
    logoUrl: '/assets/logos/eur-logo.svg'
  },
  {
    id: 'usdd-live',
    symbol: 'USDD',
    issuer: TOKEN_ISSUER,
    name: 'USDD',
    assetClass: 'stable',
    category: 'settlement',
    description: 'USD derivative settlement token. 100K circulating.',
    icon: '$',
    logoUrl: '/assets/logos/xusd-logo.svg'
  },
  // ─── RWA / Commodity ──────────────────────────
  {
    id: 'gold-live',
    symbol: 'GOLD',
    issuer: TOKEN_ISSUER,
    name: 'GOLD (IOU)',
    assetClass: 'rwa',
    category: 'representational',
    description: 'Gold-referenced IOU. 20M circulating · 8 trustlines · 5 holders. DEX active.',
    icon: 'Au',
    logoUrl: '/assets/logos/gold-logo.svg'
  },
  // ─── Meme / Community ─────────────────────────
  {
    id: 'donk-live',
    symbol: 'DONK',
    issuer: TOKEN_ISSUER,
    name: 'DONK',
    assetClass: 'meme',
    category: 'meme',
    description: 'Community meme token. 2M circulating · 5 trustlines · 3 holders.',
    icon: '🫏',
    logoUrl: '/assets/logos/donk-logo.svg'
  },
  {
    id: 'drunks-live',
    symbol: 'DRUNKS',
    issuer: TOKEN_ISSUER,
    name: 'DRUNKS',
    assetClass: 'meme',
    category: 'meme',
    description: 'Community meme token. 50B supply · 2 trustlines · 2 holders.',
    icon: '🍺',
    logoUrl: '/assets/logos/drunks-logo.svg'
  },

  // ═══════════════════════════════════════════════
  // PLANNED — Not yet issued on XRPL
  // Will be issued from TREASURY (rpP12) when ready
  // ═══════════════════════════════════════════════
  {
    id: 'aurg-planned',
    symbol: 'AURG',
    issuer: TREASURY,
    name: 'Aurum Gram',
    assetClass: 'rwa',
    category: 'representational',
    description: 'PLANNED — Weight-referenced gold (1 AURG = 1g). Already on Stellar. XRPL issuance pending.',
    icon: 'Au',
    logoUrl: '/assets/logos/aurg-logo.svg'
  },
  {
    id: 'unykorn-planned',
    symbol: 'UNYKORN',
    issuer: TREASURY,
    name: 'UNYKORN',
    assetClass: 'utility',
    category: 'governance',
    description: 'PLANNED — Governance and utility token for UnyKorn Protocol.',
    icon: '🦄',
    logoUrl: '/assets/logos/unykorn-token-logo.svg'
  },
];

function safeParse(json: string | null): unknown {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export async function loadAssetRegistry(): Promise<XrplIssuedAsset[]> {
  const stored = await getStorageItem(STORAGE_KEY);
  const parsed = safeParse(stored);
  if (!Array.isArray(parsed)) return [...DEFAULT_ASSETS];

  const cleaned: XrplIssuedAsset[] = parsed
    .filter((x) => typeof x === 'object' && x !== null)
    .map((x) => x as Partial<XrplIssuedAsset>)
    .filter((x) => typeof x.id === 'string' && typeof x.symbol === 'string' && typeof x.issuer === 'string' && typeof x.name === 'string')
    .map((x) => ({
      id: x.id as string,
      symbol: (x.symbol as string).toUpperCase(),
      issuer: x.issuer as string,
      name: x.name as string,
      assetClass: (x.assetClass as AssetClass) || 'other',
      description: x.description,
      icon: x.icon
    }));

  return cleaned.length ? cleaned : [...DEFAULT_ASSETS];
}

export async function saveAssetRegistry(assets: XrplIssuedAsset[]): Promise<void> {
  await setStorageItem(STORAGE_KEY, JSON.stringify(assets));
}

export function upsertAsset(assets: XrplIssuedAsset[], asset: XrplIssuedAsset): XrplIssuedAsset[] {
  const idx = assets.findIndex((a) => a.id === asset.id);
  if (idx === -1) return [...assets, asset];
  const copy = [...assets];
  copy[idx] = asset;
  return copy;
}

export function removeAsset(assets: XrplIssuedAsset[], id: string): XrplIssuedAsset[] {
  return assets.filter((a) => a.id !== id);
}
